import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  adaptiveApi,
  controlsApi,
  type ControlsStatus,
  type DailyPerformanceDay,
  type ParameterOverrideView,
} from '../api/client'
import { adaptiveModeWord } from '../lib/adaptiveMode'
import './AdaptiveTuningZone.css'

export function AdaptiveTuningZone() {
  const queryClient = useQueryClient()

  const perfQuery = useQuery({
    queryKey: ['adaptive-daily-performance'],
    queryFn: () => adaptiveApi.dailyPerformance(14),
    refetchInterval: 60_000,
  })

  const overridesQuery = useQuery({
    queryKey: ['adaptive-parameter-overrides'],
    queryFn: () => adaptiveApi.parameterOverrides(),
    refetchInterval: 60_000,
  })

  const approveMutation = useMutation({
    mutationFn: (id: number) => adaptiveApi.approveOverride(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adaptive-parameter-overrides'] })
    },
  })

  const rejectMutation = useMutation({
    mutationFn: (id: number) => adaptiveApi.rejectOverride(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adaptive-parameter-overrides'] })
    },
  })

  const statusQuery = useQuery({
    queryKey: ['adaptive-status'],
    queryFn: adaptiveApi.status,
    refetchInterval: 60_000,
  })
  const runsQuery = useQuery({
    queryKey: ['adaptive-runs'],
    queryFn: () => adaptiveApi.runs(14),
    refetchInterval: 60_000,
  })
  const status = statusQuery.data ?? null
  const runs = runsQuery.data?.runs ?? []

  const days: DailyPerformanceDay[] = perfQuery.data?.days ?? []
  const overrides: ParameterOverrideView[] = overridesQuery.data?.overrides ?? []
  const latest = days[0] ?? null
  const queued = overrides.filter((o) => o.status === 'queued')

  const isLoading = perfQuery.isLoading && overridesQuery.isLoading
  const hasError = perfQuery.isError || overridesQuery.isError
  const errorMsg = hasError
    ? [
        perfQuery.isError ? String(perfQuery.error) : null,
        overridesQuery.isError ? String(overridesQuery.error) : null,
      ]
        .filter(Boolean)
        .join('; ')
    : null

  if (isLoading) {
    return (
      <div className="at-zone">
        <p className="at-muted">Loading adaptive tuning data…</p>
      </div>
    )
  }

  return (
    <div className="at-zone">
      {errorMsg && (
        <div className="at-error" role="alert">
          {errorMsg}
        </div>
      )}

      {status && (
        <div className={`adaptive-mode-banner mode-${adaptiveModeWord(status).toLowerCase()}`}>
          <strong>ADAPTIVE: {adaptiveModeWord(status)}</strong>
          {status.mode_is_authoritative && status.mode === 'full' &&
            ' — auto-apply enabled'}
          {status.paused && ' · EXTERNAL APIS PAUSED (dispatch suppressed)'}
          <span className="adaptive-next-run">
            {' '}· last run {formatAdaptiveTime(status.last_run?.finished_at).full}
            {' '}· next {formatAdaptiveTime(status.next_run_at).local}
            {' '}· dispatches {status.dispatches_today}/{status.max_per_day} today
          </span>
        </div>
      )}

      <div className="adaptive-run-strip">
        {runs.slice().reverse().map((r) => (
          <span
            key={r.id}
            className={`run-cell run-${r.status} dispatch-${r.dispatch_outcome ?? 'none'}`}
            title={[
              `${r.run_date}: ${r.status}`,
              r.abort_reason ? `abort: ${r.abort_reason}` : null,
              `started ${formatAdaptiveTime(r.started_at).full}`,
              r.finished_at ? `finished ${formatAdaptiveTime(r.finished_at).full}` : 'NOT FINISHED',
              r.dispatch_outcome ? `dispatch: ${r.dispatch_outcome}` : null,
              r.corrective_status ? `corrective: ${r.corrective_status}` : null,
            ].filter(Boolean).join('\n')}
          >
            {r.status === 'ok' ? (r.dispatch_outcome === 'dispatched' ? '▲' : '●')
             : r.status?.startsWith('skipped') ? '◦' : '✕'}
          </span>
        ))}
      </div>

      {/* External-API spending kill switch */}
      <ExternalApiControlPanel />

      {/* Tile grid: two summary tiles */}
      <div className="at-grid">
        <div className="card at-tile">
          <div className="at-tile__label">Latest day vs expectations</div>
          {latest ? (
            <>
              <div className="at-tile__value">
                {fmtPct(latest.summary['daily_return'])}
              </div>
              <div className={`at-badge ${latest.triggered ? 'at-badge--warn' : 'at-badge--ok'}`}>
                {latest.triggered
                  ? `Triggered — ${latest.gap_kind ?? 'unknown'}`
                  : 'Within expectations'}
              </div>
              <div className="at-tile__date at-muted">{latest.trade_date}</div>
            </>
          ) : (
            <div className="at-tile__value">—</div>
          )}
        </div>

        <div className="card at-tile">
          <div className="at-tile__label">Pending parameter changes</div>
          <div className="at-tile__value">{queued.length}</div>
          {queued.length > 0 && (
            <div className="at-muted at-tile__sublabel">awaiting approval</div>
          )}
        </div>
      </div>

      {/* Override list */}
      {overrides.length === 0 ? (
        <p className="at-muted at-empty">No parameter overrides recorded yet.</p>
      ) : (
        <>
          <div className="at-overrides__heading at-muted">Parameter overrides</div>
          <div className="at-overrides">
          {overrides.map((o) => (
            <div
              key={o.id}
              className={`at-override at-override--${o.status}`}
            >
              <div className="at-override__info">
                <code className="at-override__key">{o.param_key}</code>
                <span className="at-override__delta">→ {o.value}</span>
                <span className={`at-status at-status--${o.status}`}>
                  {o.status}
                </span>
                {o.is_stale && (
                  <span className="stale-badge"
                        title={`queued ${o.age_trading_days} trading days`}>
                    stale · {o.age_trading_days}td
                  </span>
                )}
              </div>
              {o.status === 'queued' && (
                <div className="at-override__actions">
                  <button
                    type="button"
                    className="at-btn at-btn--approve"
                    onClick={() => approveMutation.mutate(o.id)}
                    disabled={
                      approveMutation.isPending || rejectMutation.isPending
                    }
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="at-btn at-btn--reject"
                    onClick={() => rejectMutation.mutate(o.id)}
                    disabled={
                      approveMutation.isPending || rejectMutation.isPending
                    }
                  >
                    Reject
                  </button>
                </div>
              )}
              <div className="at-override__dates at-muted">
                <span title="When this override was proposed">
                  proposed {fmtDateTime(o.created_at)}
                </span>
                {o.applied_at && (
                  <span title="When this override was applied">
                    {' '}· applied {fmtDateTime(o.applied_at)}
                  </span>
                )}
                <span title="Trading-day window this override is effective for">
                  {' '}· effective {fmtDateOnly(o.effective_date)}
                  {' '}–&nbsp;{fmtDateOnly(o.expires_date)}
                </span>
              </div>
              {o.reason && (
                <div className="at-override__reason at-muted">{o.reason}</div>
              )}
            </div>
          ))}
          </div>
        </>
      )}
    </div>
  )
}

function fmtPct(v: number | string | null | undefined): string {
  if (v === undefined || v === null || v === '') return '—'
  const n = Number(v)
  if (!isFinite(n)) return '—'
  return `${(n * 100).toFixed(2)}%`
}

function formatAdaptiveTime(iso: string | null | undefined): {
  local: string; utc: string; relative: string; full: string
} {
  if (!iso) return { local: '—', utc: '', relative: '', full: '—' }
  const d = new Date(iso)
  if (isNaN(d.getTime())) return { local: iso, utc: '', relative: '', full: iso }
  const local = d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    timeZoneName: 'short',
  })
  const utc = d.toLocaleString('en-GB', {
    hour: '2-digit', minute: '2-digit', timeZone: 'UTC',
  }) + ' UTC'
  const mins = Math.round((Date.now() - d.getTime()) / 60000)
  const relative =
    mins < 60 ? `${mins}m ago`
    : mins < 1440 ? `${Math.round(mins / 60)}h ago`
    : `${Math.round(mins / 1440)}d ago`
  return { local, utc, relative, full: `${local} (${utc}) — ${relative}` }
}

function fmtDateTime(v: string | null | undefined): string {
  return formatAdaptiveTime(v).full
}

/** Compact, date-only formatting for Date columns (effective/expires). */
function fmtDateOnly(v: string | null | undefined): string {
  if (!v) return '—'
  const d = new Date(v)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  })
}

/**
 * External API Spending control panel — the operator-facing kill switch that
 * pauses/resumes all outbound paid-API calls (LLM, news, market data), plus a
 * tracking list of recent backtest runs. Self-contained and resilient: a failed
 * status fetch renders a non-blocking error and never crashes the parent zone.
 */
function ExternalApiControlPanel() {
  const queryClient = useQueryClient()
  const [confirming, setConfirming] = useState(false)

  const statusQuery = useQuery({
    queryKey: ['controls-status'],
    queryFn: () => controlsApi.getStatus(),
    refetchInterval: 30_000,
  })

  const onMutated = (data: ControlsStatus) => {
    // The POST endpoints return the fresh status, so seed the cache directly
    // and then revalidate to pick up any concurrent operator changes.
    queryClient.setQueryData(['controls-status'], data)
    queryClient.invalidateQueries({ queryKey: ['controls-status'] })
  }

  const pauseMutation = useMutation({
    mutationFn: () => controlsApi.pauseExternal(),
    onSuccess: (data) => {
      onMutated(data)
      setConfirming(false)
    },
  })

  const resumeMutation = useMutation({
    mutationFn: () => controlsApi.resumeExternal(),
    onSuccess: onMutated,
  })

  const status = statusQuery.data
  const paused = status?.external_apis_paused ?? false
  const busy = pauseMutation.isPending || resumeMutation.isPending
  const runs = status?.recent_backtest_runs ?? []

  return (
    <div className="card at-tile at-controls">
      <div className="at-tile__label">External API Spending</div>

      {statusQuery.isLoading ? (
        <p className="at-muted">Loading control state…</p>
      ) : statusQuery.isError ? (
        <div className="at-error" role="alert">
          Could not load external-API control state: {String(statusQuery.error)}
        </div>
      ) : (
        <>
          <div className="at-controls__row">
            <span
              className={`at-badge ${paused ? 'at-badge--warn' : 'at-badge--ok'}`}
            >
              {paused ? 'Paused' : 'Active'}
            </span>

            {paused ? (
              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={() => resumeMutation.mutate()}
                disabled={busy}
              >
                {resumeMutation.isPending ? 'Resuming…' : 'Resume'}
              </button>
            ) : confirming ? (
              <div className="at-controls__actions">
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => pauseMutation.mutate()}
                  disabled={busy}
                >
                  {pauseMutation.isPending ? 'Pausing…' : 'Confirm pause'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setConfirming(false)}
                  disabled={busy}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => setConfirming(true)}
                disabled={busy}
              >
                Pause
              </button>
            )}
          </div>

          {confirming && !paused && (
            <p className="at-muted at-controls__hint">
              Pausing halts all outbound paid-API calls (LLM, news, market
              data). Confirm to apply.
            </p>
          )}

          {pauseMutation.isError && (
            <div className="at-error" role="alert">
              Pause failed: {String(pauseMutation.error)}
            </div>
          )}
          {resumeMutation.isError && (
            <div className="at-error" role="alert">
              Resume failed: {String(resumeMutation.error)}
            </div>
          )}

          {(status?.updated_by || status?.updated_at) && (
            <div className="at-muted at-controls__meta">
              {status?.updated_by && <>by {status.updated_by} · </>}
              {fmtDateTime(status?.updated_at)}
            </div>
          )}

          <div className="at-overrides__heading at-muted">
            Recent backtest runs
          </div>
          <div className="at-controls__runs">
            {runs.length === 0 ? (
              <p className="at-muted at-empty">No recent backtest runs</p>
            ) : (
              runs.map((r) => (
                <div key={r.run_id} className="at-controls__run">
                  <code className="at-override__key">{r.run_id}</code>
                  <span className={`at-status at-status--${r.status}`}>
                    {r.status}
                  </span>
                  <span className="at-muted">{fmtDateTime(r.started_at)}</span>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  )
}
